import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Alert,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../common/button/commonButton";
import ReusableTable from "../../common/table/CommonTable";
import CustomTextField from "../../common/textfield/CommonTextField";
import {
  addBlock,
  deleteBlock,
  fetchAllBlocks,
  updateBlock,
} from "../../redux/block/blockAction";

const BlockManagementForm = () => {
  const [blocks, setBlocks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [blockToDelete, setBlockToDelete] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const loginValues = useSelector((state) => state.values?.loginValues || []);
  const userInfo = loginValues[0]?.user || {};

  const permissions =
    typeof userInfo?.access === "string"
      ? JSON.parse(userInfo?.access)
      : userInfo?.access || {};

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
  } = useForm();

  const watchStatus = watch("status", true);

  const blockList = useSelector((state) => state?.block?.fetchAllBlocks || {});
  const blockDelete = useSelector((state) => state?.block?.deleteBlock || {});
  const blockAdd = useSelector((state) => state?.block?.addBlock || {});
  const blockUpdate = useSelector((state) => state?.block?.updateBlock || {});

  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = blockList?.block?.totalPages;

  const normalizeBlockName = useCallback((name) => {
    if (!name) return "";
    return name.replace(/\s+/g, " ").trim().toLowerCase();
  }, []);

  const blockNameExists = useCallback(
    (name, currentBlockId = null) => {
      const normalizedNewName = normalizeBlockName(name);
      return blocks.some((block) => {
        if (currentBlockId && block.id === currentBlockId) {
          return false;
        }
        const normalizedExistingName = normalizeBlockName(block.block_name);
        return normalizedNewName === normalizedExistingName;
      });
    },
    [blocks, normalizeBlockName]
  );

  const fetchData = useCallback(
    (page) => {
      setTableLoading(true);
      dispatch(fetchAllBlocks(page));
      setTimeout(() => {
        setTableLoading(false);
      }, 500);
    },
    [dispatch]
  );

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);
  useEffect(() => {
    if (blockList?.Blocks?.data) {
      setBlocks(
        blockList.Blocks.data.map((block, index) => ({
          id: block.id,
          serial_no: index + 1,
          block_name: block.block_name,
          zone_name: block.zone_name,
          block_status: block.block_status,
        }))
      );
    }
  }, [blockList]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  useEffect(() => {
    if (blockAdd?.success) {
      setSnackbarMessage("Block added successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchAllBlocks(currentPage));
    } else if (blockAdd?.error) {
      setSnackbarMessage(blockAdd.error || "Failed to add block");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [blockAdd, dispatch, currentPage]);

  useEffect(() => {
    if (blockUpdate?.success) {
      setSnackbarMessage("Block updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchAllBlocks(currentPage));
    } else if (blockUpdate?.error) {
      setSnackbarMessage(blockUpdate.error || "Failed to update block");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [blockUpdate, dispatch, currentPage]);

  useEffect(() => {
    if (blockDelete?.success) {
      setSnackbarMessage("Block deleted successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      dispatch(fetchAllBlocks(currentPage));
    } else if (blockDelete?.error) {
      setSnackbarMessage(blockDelete.error || "Failed to delete block");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [blockDelete, dispatch, currentPage]);

  useEffect(() => {
    if (openDialog && dialogMode === "edit" && selectedBlock) {
      setValue("block_name", selectedBlock.block_name || "");
      setValue("zone_name", selectedBlock.zone_name || "");
      setValue(
        "status",
        selectedBlock.block_status === 1 || selectedBlock.block_status === true
      );
    }
  }, [openDialog, dialogMode, selectedBlock, setValue]);

  const columns = [
    { id: "serial_no", label: "Serial No", sortable: true },
    { id: "block_name", label: "Block Name", sortable: true },
    { id: "zone_name", label: "Zone Name", sortable: true },
    {
      id: "block_status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? "Active" : "Inactive"}
          color={value === 1 ? "success" : "error"}
          variant="filled"
          size="small"
          sx={{
            borderRadius: "16px",
            fontWeight: "bold",
            minWidth: "80px",
          }}
        />
      ),
    },
  ];

  const handleOpenDialog = (mode, block = null) => {
    setDialogMode(mode);
    setSelectedBlock(block);
    reset();

    if (mode === "edit" && block) {
      setValue("block_name", block.block_name || "");
      setValue("zone_name", block.zone_name || "");
      setValue("status", block.block_status === 1);
    } else {
      setValue("block_name", "");
      setValue("zone_name", "");
      setValue("status", true);
    }

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    reset();
    clearErrors();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const validateBlockName = (e) => {
    const value = e.target.value.replace(/\s+/g, " ").trim();

    if (!value) {
      setError("block_name", {
        type: "manual",
        message: "Block name cannot be empty",
      });
      return;
    }

    clearErrors("block_name");

    const isDuplicate = blockNameExists(
      value,
      dialogMode === "edit" ? selectedBlock?.id : null
    );

    if (isDuplicate) {
      setError("block_name", {
        type: "manual",
        message: "Block name already exists",
      });
    }
  };

  const validateZoneName = (e) => {
    const value = e.target.value.replace(/\s+/g, " ").trim();

    if (!value) {
      setError("zone_name", {
        type: "manual",
        message: "Zone name cannot be empty",
      });
      return;
    }

    clearErrors("zone_name");
  };

  const onSubmit = (data) => {
    const block_name = data.block_name
      ? data.block_name.replace(/\s+/g, " ").trim()
      : "";
    const zone_name = data.zone_name
      ? data.zone_name.replace(/\s+/g, " ").trim()
      : "";

    if (!block_name) {
      setError("block_name", {
        type: "manual",
        message: "Block name cannot be empty",
      });
      return;
    }

    if (!zone_name) {
      setError("zone_name", {
        type: "manual",
        message: "Zone name cannot be empty",
      });
      return;
    }

    const isDuplicate = blockNameExists(
      block_name,
      dialogMode === "edit" ? selectedBlock?.id : null
    );

    if (isDuplicate) {
      setError("block_name", {
        type: "manual",
        message: "Block name already exists",
      });
      return;
    }

    const formData = {
      block_name,
      zone_name,
    };

    if (dialogMode === "add") {
      dispatch(
        addBlock({
          ...formData,
          block_status: 1,
        })
      );
    } else if (dialogMode === "edit") {
      dispatch(
        updateBlock({
          id: selectedBlock.id,
          ...formData,
          block_status: watchStatus ? 1 : 2,
        })
      );
    }

    handleCloseDialog();
  };

  const handleOpenDeleteDialog = (block) => {
    setBlockToDelete(block);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setBlockToDelete(null);
  };

  const handleDeleteBlock = () => {
    if (blockToDelete) {
      dispatch(deleteBlock(blockToDelete.id));
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Block Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          sx={{
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "bold",
            color: "white",
            bgcolor: "#2c90f7",
          }}
        >
          Add New Block
        </Button>
      </Box>

      <ReusableTable
        columns={columns}
        data={blocks}
        isLoading={tableLoading}
        title="Blocks"
        onEditClick={(block) => handleOpenDialog("edit", block)}
        onDeleteClick={(block) => handleOpenDeleteDialog(block)}
        customEmptyMessage="No blocks found. Add a new block to get started."
        headerColor="#000000"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        withActions={true}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#129bab",
            color: "white",
          }}
        >
          {dialogMode === "add" ? "Add New Block" : "Edit Block"}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Paper elevation={0} sx={{ p: 2 }}>
              {/* Block Name Field */}
              <CustomTextField
                label="Block Name"
                value={watch("block_name", "")}
                onChange={(e) => {
                  setValue("block_name", e.target.value);
                }}
                error={!!errors.block_name}
                helperText={errors.block_name?.message}
                fullWidth
                margin="normal"
                type="text"
                inputProps={{
                  maxLength: 50,
                  onBlur: validateBlockName,
                }}
                required
              />

              {/* Spacer between fields */}
              <Box sx={{ height: "24px" }} />

              {/* Zone Name Field */}
              <CustomTextField
                label="Zone Name"
                value={watch("zone_name", "")}
                onChange={(e) => {
                  setValue("zone_name", e.target.value);
                }}
                error={!!errors.zone_name}
                helperText={errors.zone_name?.message}
                fullWidth
                margin="normal"
                type="text"
                inputProps={{
                  maxLength: 50,
                  onBlur: validateZoneName,
                }}
                required
              />

              {dialogMode === "edit" && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={watchStatus}
                        onChange={(e) => setValue("status", e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Box>
              )}
            </Paper>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#129bab",
              justifyContent: "center",
            }}
          >
            <CustomButton
              onClick={handleCloseDialog}
              type="button"
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
            />
            <CustomButton
              type="submit"
              text={dialogMode === "add" ? "Add" : "Update"}
              variant="contained"
              disableElevation
              size="small"
              startIcon={<AddIcon />}
              disabled={blockAdd?.inProgress || blockUpdate?.inProgress}
              loading={blockAdd?.inProgress || blockUpdate?.inProgress}
            />
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{ bgcolor: "#f44336", color: "white" }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" sx={{ mt: 2 }}>
            Are you sure you want to delete the block "
            {blockToDelete?.block_name?.replace(/\s+/g, " ").trim() || ""}
            "? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <CustomButton
            onClick={handleCloseDeleteDialog}
            type="button"
            text="Cancel"
            variant="outlined"
            disableElevation
            size="small"
          />
          <CustomButton
            onClick={handleDeleteBlock}
            type="button"
            text="Delete"
            variant="contained"
            disableElevation
            size="small"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={blockDelete?.inProgress}
            loading={blockDelete?.inProgress}
          />
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlockManagementForm;
